import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import i18next from 'i18next';
import Button from 'react-bootstrap-button-loader';

import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import TextField from "@material-ui/core/TextField";
import { Popup } from "@progress/kendo-react-popup";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { IntlProvider, LocalizationProvider } from "@progress/kendo-react-intl";
import InputLabel from '@material-ui/core/InputLabel';
import { loadLocalization } from '../../../utils/kendoLocalization';
import { GetLanguage } from '../../../utils/language';
import AlertAlgo from '../../controls/AlertSnack'
import { saveException } from '../../../actions/availability';
import * as StatusCode from '../../../scripts/StatusCode';
import DateFnsUtils from '@date-io/date-fns';
import moment from "moment";
import "moment/locale/fr";
import { MuiPickersUtilsProvider, KeyboardTimePicker } from '@material-ui/pickers';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

import { isEmpty, formatDate, formatDateTimeNET, convertToDate } from '../../../utils';
import styles from '../../../styles/scheduleAvailability.module.css';

import { withNamespaces } from 'react-i18next';

loadLocalization();
moment.locale("fr");

const useStyles = makeStyles((theme) => ({
    rootProfile: {
        overflow: 'auto'
    },
    appBar: {
        position: 'relative',
        backgroundColor: 'var(--color1) !important',
        color: 'var(--color2) !important'
    },
    title: {
        marginLeft: theme.spacing(2),
        flex: 1,
    },
    divForm: {
        padding: '10px'
    },
    date: {
        marginBottom: '15px'
    },
    divButtons: {
        textAlign: 'center',
        marginTop: '10px'
    },
    timePicker: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    }
}));

function ExceptionForm(props) {
    const classes = useStyles();

    let currentDate = new Date();
    currentDate = currentDate.toLocaleDateString('en-CA');
    const hourDefault = '08:00';

    const [exceptionId, setExceptionId] = useState(props.exceptionId);
    const [data, setData] = useState(props.data);
    const [isPartial, setIsPartial] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [comment, setComment] = useState(null);
    const [locale, setLocale] = useState(GetLanguage());

    const alertRef = useRef(null);

    useEffect(() => {

    }, []);

    useEffect(() => {
        if (exceptionId > 0) {
            const dataFiltered = data.filter(x => x.id == exceptionId);

            setData(dataFiltered);
        }
    }, [exceptionId]);

    useEffect(() => {
        if (exceptionId > 0 && data.length == 1) {
            setStartDate(convertToDate(data[0].startDate));
            setEndDate(convertToDate(data[0].endDate));
            setComment(data[0].comment);
            setIsPartial(data[0].isPartial);
            setStartTime(data[0].startTime != null ? new Date(data[0].startTime) : null);
            setEndTime(data[0].endTime != null ? new Date(data[0].endTime) : null);
        }
    }, [data]);

    useEffect(() => {
        if (isPartial) {
            setEndDate(null);

            // We need to do this validation to not override the load data in edit mode
            if (startTime == null) {
                setStartTime(`${currentDate} ${hourDefault}`);
            }
            if (endTime == null) {
                setEndTime(`${currentDate} ${hourDefault}`);
            }
        } else {
            setStartTime(null);
            setEndTime(null);
        }
    }, [isPartial]);

    const handleReloadException = () => {
        props.handleReloadException();
    }

    const isValidData = () => {
        let message = '';

        if (isEmpty(startDate)) {
            message = i18next.t('scheduleAvailability:exception.message.startDate-form');
        } else if (!isPartial && isEmpty(endDate)) {
            message = i18next.t('scheduleAvailability:exception.message.endDate-form');
        } else if (!isEmpty(endDate) && new Date(startDate) > new Date(endDate)) {
            message = i18next.t('scheduleAvailability:exception.message.endDateGreaterThanStartDate');
        } else if (isPartial && isEmpty(startTime)) {
            message = i18next.t('scheduleAvailability:exception.message.startTime-form');
        } else if (isPartial && isEmpty(endTime)) {
            message = i18next.t('scheduleAvailability:exception.message.endTime-form');
        } else if (!isEmpty(endTime) && new Date(formatDate(startTime)) >= new Date(formatDate(endTime))) {
            message = i18next.t('scheduleAvailability:exception.message.endTimeGreaterThanStartTime');
        }

        if (message.length > 0) {
            showMessageError(message);
            return false;
        }

        return true;
    }

    const handleSave = async () => {
        if (isValidData()) {
            const formattedStartTime = startTime != null ? formatDateTimeNET(formatDate(startTime)) : null;
            const formattedEndTime = startTime != null ? formatDateTimeNET(formatDate(endTime)) : null;
            const response = await saveException(exceptionId, startDate, endDate, formattedStartTime, formattedEndTime, comment, isPartial);

            if (response == null || (response != null && response.status != StatusCode.STATUS_OK)) {
                // Error
                showMessageError(i18next.t('error:generic-error'));
                return;
            }

            handleReloadException();
        }
    }

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const onChangeIsPartial = (event) => {
        setIsPartial(event.target.checked);
    }

    const onStartDateChange = (event) => {
        setStartDate(event.target.value);
    }

    const onEndDateChange = (event) => {
        setEndDate(event.target.value);
    }

    const onStartTimeChange = (date) => {
        setStartTime(formatDate(date));
    }

    const onEndTimeChange = (date) => {
        setEndTime(formatDate(date));
    }

    const onChangeComment = (event) => {
        setComment(event.target.value);
    }

    let _el;

    return (
        <div id={"divExceptionForm"} className={styles.rootSchedulePopBody}>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <FormControl className={styles.switchComponent}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isPartial}
                                    onChange={onChangeIsPartial}
                                    name="isPartial"
                                    variant="success"
                                />
                            }
                            label={i18next.t("scheduleAvailability:exception.list.partial")}
                            labelPlacement="start"
                        />
                    </FormControl>

                    <div className={classes.date} ref={el => (_el = el)}>
                        <InputLabel className={"labelDatePicker required"} htmlFor="startDate">{i18next.t('scheduleAvailability:exception.list.startDate')}</InputLabel>
                        <DatePicker
                            className={classes.timePicker + ' required'}
                            value={startDate}
                            name="startDate"
                            onChange={onStartDateChange}
                            format="yyyy-MM-dd"
                            popup={props => <Popup {...props} appendTo={_el} />} />
                    </div>

                    {!isPartial && <div className={classes.date} ref={el => (_el = el)}>
                        <InputLabel className={"labelDatePicker required"} htmlFor="endDate">{i18next.t('scheduleAvailability:exception.list.endDate')}</InputLabel>
                        <DatePicker
                            className={classes.timePicker + ' required'}
                            value={endDate}
                            name="endDate"
                            onChange={onEndDateChange}
                            format="yyyy-MM-dd"
                            popup={props => <Popup {...props} appendTo={_el} />} />
                    </div>}

                    {isPartial && <FormControl className={classes.date} fullWidth>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardTimePicker
                                onKeyDown={(event) => { event.preventDefault() }}
                                invalidDateMessage={i18next.t('common:incorrectTime')}
                                ampm={false}
                                margin="normal"
                                id="startTimePicker"
                                label={i18next.t('scheduleAvailability:exception.list.startTime')}
                                value={startTime}
                                minutesStep={15}
                                onChange={onStartTimeChange}
                                className={classes.timePicker + ' required'}
                                cancelLabel={i18next.t('buttons:cancel')}
                                keyboardIcon={<AccessTimeIcon />}
                                KeyboardButtonProps={{
                                    'aria-label': 'change time',
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </FormControl>}

                    {isPartial && <FormControl className={classes.date} fullWidth>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardTimePicker
                                onKeyDown={(event) => { event.preventDefault() }}
                                invalidDateMessage={i18next.t('common:incorrectTime')}
                                ampm={false}
                                margin="normal"
                                id="endTimePicker"
                                label={i18next.t('scheduleAvailability:exception.list.endTime')}
                                value={endTime}
                                minutesStep={15}
                                onChange={onEndTimeChange}
                                className={classes.timePicker + ' required'}
                                cancelLabel={i18next.t('buttons:cancel')}
                                keyboardIcon={<AccessTimeIcon />}
                                KeyboardButtonProps={{
                                    'aria-label': 'change time',
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </FormControl>}

                    <FormControl fullWidth>
                        <TextField
                            id="comment"
                            label={i18next.t('scheduleAvailability:exception.list.comment')}
                            value={comment || ''}
                            multiline
                            minRows={4}
                            onChange={onChangeComment}
                        />
                    </FormControl>
                    <div className="divButtons">
                        <Button onClick={handleSave} variant="success" className="saveButton">
                            {i18next.t('buttons:save')}
                        </Button>
                    </div>
                    <AlertAlgo ref={alertRef} />
                </IntlProvider>
            </LocalizationProvider>
        </div>
    );
}

export default withNamespaces(['buttons', 'scheduleAvailability', 'error', 'common'], { wait: true })(ExceptionForm);